import { useState, useEffect } from 'react';
import { Calendar, Clock, Video, Edit3, Trash2, Plus, Eye, Filter, Search, X } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { ScheduleAppointmentModal } from './ScheduleAppointmentModal';
import { 
  useAppointments, 
  useUpdateAppointment, 
  useCancelAppointment 
} from '../../lib/hooks/useAppointments';
import type { Appointment } from '../../lib/api/appointments';
import { apiClient } from '../../lib/api-client';
import { VideoCallScreen } from '../shared/VideoCallScreen';

interface AppointmentsTabProps {
  onRefresh?: () => void;
}

export function AppointmentsTab({ onRefresh }: AppointmentsTabProps) {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<number | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  

  // Form state for editing
  const [editFormData, setEditFormData] = useState({
    scheduled_date: '',
    scheduled_time: '',
    notes: ''
  });

  // Fetch data
  const { data: appointments, isLoading, error, refetch } = useAppointments();
  const updateMutation = useUpdateAppointment();
  const cancelMutation = useCancelAppointment();

  // Get current user ID
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiClient.get('/auth/me');
        setCurrentUserId(response.id);
      } catch (error) {
        console.error('Failed to load current user:', error);
      }
    };
    loadCurrentUser();
  }, []);

  

  // Filter appointments
  const filteredAppointments = (appointments || []).filter(apt => {
    const matchesSearch = searchQuery === '' || 
      apt.patient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      apt.doctor_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // ✅ FIX: Sort appointments by scheduled_date in ascending order
  const sortedAppointments = [...filteredAppointments].sort((a, b) => {
    return new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime();
  });

  // Get current time for comparison
  const now = new Date();

  const upcomingAppointments = sortedAppointments.filter(apt => {
    const startTime = new Date(apt.scheduled_date);

    // Use actual duration if available
    const endTime = new Date(
      startTime.getTime() + (apt.duration_minutes ?? 30) * 60 * 1000
    );

    const isUpcomingOrRunning = endTime >= now;

    const isActiveStatus = ['Scheduled', 'Confirmed', 'In Progress'].includes(apt.status);
    return isUpcomingOrRunning && isActiveStatus;
  });
  
  const pastAppointments = sortedAppointments.filter(apt => {
    const aptDateTime = new Date(apt.scheduled_date);
    const isPast = aptDateTime < now;
    const isCompletedStatus = ['Completed', 'Cancelled', 'No Show', 'Rescheduled'].includes(apt.status);
    return isCompletedStatus || isPast;
  });

  // Handlers
  const openEditModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    const scheduledDate = new Date(appointment.scheduled_date);
    setEditFormData({
      scheduled_date: scheduledDate.toISOString().split('T')[0],
      scheduled_time: scheduledDate.toTimeString().slice(0, 5),
      notes: appointment.notes || ''
    });
    setIsEditModalOpen(true);
  };

  const openViewModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsViewModalOpen(true);
  };

  const handleUpdateAppointment = async () => {
    if (!selectedAppointment) return;

    const updates: any = {};
    
    if (editFormData.scheduled_date && editFormData.scheduled_time) {
      updates.scheduled_date = `${editFormData.scheduled_date}T${editFormData.scheduled_time}:00`;
    }
    if (editFormData.notes) {
      updates.notes = editFormData.notes;
    }

    try {
      await updateMutation.mutateAsync({
        id: selectedAppointment.id,
        data: updates
      });
      toast.success('Appointment updated successfully!');
      setIsEditModalOpen(false);
      setSelectedAppointment(null);
      refetch();
      onRefresh?.();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update appointment');
    }
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;

    try {
      await cancelMutation.mutateAsync(appointmentToCancel);
      toast.success('Appointment cancelled successfully');
      setAppointmentToCancel(null);
      refetch();
      onRefresh?.();
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel appointment');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'scheduled':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      case 'confirmed':
        return 'bg-green-500/10 text-green-600 border-green-500';
      case 'in progress':
        return 'bg-yellow-500/10 text-yellow-600 border-yellow-500';
      case 'completed':
        return 'bg-gray-500/10 text-gray-600 border-gray-500';
      case 'cancelled':
        return 'bg-red-500/10 text-red-600 border-red-500';
      case 'no show':
        return 'bg-orange-500/10 text-orange-600 border-orange-500';
      case 'rescheduled':
        return 'bg-blue-500/10 text-blue-600 border-blue-500';
      default:
        return 'bg-secondary';
    }
  };

  // Check if PA can edit/delete an appointment
  // PA can only edit/delete appointments they created
  // Appointments scheduled by coordinator (that include PA) are view-only
  const canEditAppointment = (appointment: Appointment) => {
    if (!currentUserId) return false;
    
    // If appointment was created by this PA, they can edit it
    if (appointment.created_by_user_id === currentUserId) {
      return true;
    }
    
    // If appointment has coordinator_id and wasn't created by PA, it's view-only
    if (appointment.coordinator_id && appointment.created_by_user_id !== currentUserId) {
      return false;
    }
    
    return false;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-muted-foreground">Loading appointments...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Card className="border-destructive">
          <CardContent className="p-6">
            <p className="text-destructive">Failed to load appointments</p>
            <Button onClick={() => refetch()} className="mt-4">Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl text-primary">Appointments Management</h2>
        <Button
          onClick={() => setIsScheduleModalOpen(true)}
          className="bg-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Schedule Appointment
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by patient or doctor name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'upcoming' | 'past')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="upcoming">
            Upcoming ({upcomingAppointments.length})
          </TabsTrigger>
          <TabsTrigger value="past">
            Past ({pastAppointments.length})
          </TabsTrigger>
        </TabsList>

        {/* Upcoming Appointments */}
        <TabsContent value="upcoming" className="space-y-3 mt-4">
          {upcomingAppointments.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No upcoming appointments</p>
                <Button onClick={() => setIsScheduleModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Appointment
                </Button>
              </CardContent>
            </Card>
          ) : (
            upcomingAppointments.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                onView={() => openViewModal(appointment)}
                onEdit={canEditAppointment(appointment) ? () => openEditModal(appointment) : undefined}
                onCancel={canEditAppointment(appointment) ? () => setAppointmentToCancel(appointment.id) : undefined}
                formatDate={formatDate}
                formatTime={formatTime}
                getStatusColor={getStatusColor}
                canEdit={canEditAppointment(appointment)}
              />
            ))
          )}
        </TabsContent>

        {/* Past Appointments */}
        <TabsContent value="past" className="space-y-3 mt-4">
          {pastAppointments.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No past appointments</p>
              </CardContent>
            </Card>
          ) : (
            pastAppointments.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                onView={() => openViewModal(appointment)}
                formatDate={formatDate}
                formatTime={formatTime}
                getStatusColor={getStatusColor}
                isPast
              />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Schedule Appointment Modal */}
      <ScheduleAppointmentModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSuccess={() => {
          refetch();
          onRefresh?.();
        }}
      />

      {/* View Appointment Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-primary">Appointment Details</DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Patient</Label>
                  <p className="mt-1">{selectedAppointment.patient_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Doctor</Label>
                  <p className="mt-1">{selectedAppointment.doctor_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Date</Label>
                  <p className="mt-1">{formatDate(selectedAppointment.scheduled_date)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Time</Label>
                  <p className="mt-1">{formatTime(selectedAppointment.scheduled_date)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p className="mt-1">{selectedAppointment.appointment_type}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Duration</Label>
                  <p className="mt-1">{selectedAppointment.duration_minutes} minutes</p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Status</Label>
                <div className="mt-1">
                  <Badge variant="outline" className={getStatusColor(selectedAppointment.status)}>
                    {selectedAppointment.status}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Reason</Label>
                <p className="mt-1">{selectedAppointment.reason || 'N/A'}</p>
              </div>

              {selectedAppointment.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="mt-1">{selectedAppointment.notes}</p>
                </div>
              )}

              {selectedAppointment.consultation_notes && (
                <div>
                  <Label className="text-muted-foreground">Consultation Notes</Label>
                  <p className="mt-1">{selectedAppointment.consultation_notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-primary">Edit Appointment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Patient & Doctor (Read-only) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Patient</Label>
                <Input
                  value={selectedAppointment?.patient_name || ''}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label>Doctor</Label>
                <Input
                  value={selectedAppointment?.doctor_name || ''}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-date">Reschedule Date</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editFormData.scheduled_date}
                  onChange={(e) => setEditFormData({...editFormData, scheduled_date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-time">Reschedule Time</Label>
                <Input
                  id="edit-time"
                  type="time"
                  value={editFormData.scheduled_time}
                  onChange={(e) => setEditFormData({...editFormData, scheduled_time: e.target.value})}
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Update Notes</Label>
              <Textarea
                id="edit-notes"
                placeholder="Enter updated notes"
                value={editFormData.notes}
                onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateAppointment}
              disabled={updateMutation.isPending}
              className="bg-primary"
            >
              {updateMutation.isPending ? 'Updating...' : 'Update Appointment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={appointmentToCancel !== null} onOpenChange={() => setAppointmentToCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Appointment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this appointment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, Keep It</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelAppointment}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, Cancel Appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Appointment Card Component
interface AppointmentCardProps {
  appointment: Appointment;
  onView?: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  formatDate: (date: string) => string;
  formatTime: (date: string) => string;
  getStatusColor: (status: string) => string;
  isPast?: boolean;
  canEdit?: boolean;
}


function AppointmentCard({
  appointment,
  onView,
  onEdit,
  onCancel,
  formatDate,
  formatTime,
  getStatusColor,
  isPast = false,
  canEdit = true
}: AppointmentCardProps) {

  const [activeVideoCall, setActiveVideoCall] = useState<any>(null);

  // Helper function to check if appointment can be joined
  const canJoinAppointment = (datetime: string) => {
    const now = new Date();
    const appointmentDate = new Date(datetime);

    const appointmentEnd = new Date(
      appointmentDate.getTime() + 30 * 60 * 1000
    );

    return now >= appointmentDate && now <= appointmentEnd;
  };
  
    // Handle joining video call
    const handleJoinCall = (appointment: any) => {
      setActiveVideoCall(appointment);
    };
  
    // Handle ending video call
    const handleEndCall = () => {
      setActiveVideoCall(null);
      toast.success('Call ended successfully');
    };
  
    // Show video call screen if active
    if (activeVideoCall) {    
      return (
        <VideoCallScreen
          sessionId='TestSession#1'
          patientName={activeVideoCall.patient_name}
          doctorName={activeVideoCall.doctor_name}
          onEndCall={handleEndCall}
        />
      );
    }

  return (
    <Card className="shadow-md hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-2">
            {/* Patient & Status */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-primary">{appointment.patient_name}</h3>
                {!canEdit && appointment.coordinator_id && (
                  <Badge variant="secondary" className="text-xs">
                    Coordinator Scheduled
                  </Badge>
                )}
              </div>
              <Badge variant="outline" className={getStatusColor(appointment.status)}>
                {appointment.status}
              </Badge>
            </div>
            
            <p className="text-foreground/70">
              with {appointment.doctor_name}
            </p>

            {/* Date & Time */}
            <div className="flex items-center gap-4 text-sm text-foreground/70">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4 text-primary" />
                <span>{formatDate(appointment.scheduled_date)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-primary" />
                <span>{formatTime(appointment.scheduled_date)}</span>
              </div>
            </div>

            {/* Type */}
            <div className="flex items-center gap-2 text-sm">
              <Video className="w-4 h-4 text-primary" />
              <span className="text-foreground/70">{appointment.appointment_type}</span>
              <span className="text-foreground/50">• {appointment.duration_minutes} min</span>
            </div>

            {/* Reason */}
            {appointment.reason && (
              <p className="text-sm text-foreground/60 line-clamp-2">
                {appointment.reason}
              </p>
            )}
          </div>

          {canJoinAppointment(appointment.scheduled_date) && (
            <Button
              size="sm"
              onClick={() => handleJoinCall(appointment)}
              className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white text-xs ml-2"
            >
              <Video className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Join Call
            </Button>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2">
            {onView && (
              <Button
                size="sm"
                variant="outline"
                onClick={onView}
                className="h-8 w-8 p-0"
                title="View Details"
              >
                <Eye className="w-4 h-4" />
              </Button>
            )}
            {!isPast && onEdit && (
              <Button
                size="sm"
                variant="outline"
                onClick={onEdit}
                className="h-8 w-8 p-0"
                title="Edit Appointment"
              >
                <Edit3 className="w-4 h-4" />
              </Button>
            )}
            {!isPast && onCancel && (
              <Button
                size="sm"
                variant="outline"
                onClick={onCancel}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                title="Cancel Appointment"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}